import { FC, useEffect, useState } from "react";

import { ListenerTagRow } from "../ListenerTagRow";
import type { LiveTagStyling } from "../liveTag.types";
import { ListenerTag } from "@/features/procedures/model/procedure/ListenerTag";
import { ProcedureNodeStep } from "@/features/procedures/model/types/checklist";
import { Box } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

interface StepMeasurementListenersProps {
	activeSchemeId: number | undefined;
	autoMode: boolean;
	listenerTags: Array<ListenerTag>;
	showMeasurements: boolean;
	step: ProcedureNodeStep;
	setShowMeasurements: (show: boolean) => void;
	isActiveStep: boolean;
}

const StepMeasurementListeners: FC<StepMeasurementListenersProps> = (props) => {
	const theme = useTheme();
	const isDark = theme.palette.mode === "dark";

	const defaultStyling = {
		container: {
			background: isDark ? "#2a2a2a" : "grey",
			color: "white",
		},
	};
	const [ListenerTagRowStyling, setListenerTagRowStyling] =
		useState<LiveTagStyling>(defaultStyling);

	const alertStyling = {
		container: {
			background: isDark ? "rgba(255, 82, 82, 0.4)" : "#ff969a",
			color: isDark ? "#ffffff" : undefined,
		},
		pulseAnimation: {},
	};

	const completeStyling = {
		container: {
			background: isDark
				? `${theme.palette.success.main}40`
				: theme.palette.success.light,
			color: "white",
		},
		pulseAnimation: {},
	};

	const waitStyling: LiveTagStyling = {
		container: {
			background: isDark ? "rgba(255, 193, 7, 0.35)" : "#f9e488",
			color: isDark ? "#ffffff" : undefined,
		},
		pulseAnimation: {
			content: '""',
			position: "absolute",
			left: "0",
			height: "101%",
			width: "101%",
			transform: "translateX(-100%)",
			background: isDark
				? "linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0))"
				: "linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0))",
			animation: "pulseAnimation 1.5s linear infinite",
			"@keyframes pulseAnimation": {
				"0%": {
					transform: "translateX(-100%)",
				},
				"100%": {
					transform: "translateX(100%)",
				},
			},
		},
	};

	const skipStyling = {
		container: {
			background: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.12)",
			color: isDark ? "rgba(255, 255, 255, 0.7)" : undefined,
		},
	};

	useEffect(() => {
		if (props.step.IsSkipped || props.step.IsOverridden) {
			setListenerTagRowStyling(skipStyling);
			return;
		} else if (props.step.Status) {
			setListenerTagRowStyling(completeStyling);
			return;
		} else if (!props.isActiveStep && !props.step.Status) {
			setListenerTagRowStyling(defaultStyling);
			return;
		} else if (
			props.isActiveStep &&
			!props.step.Status &&
			(props.step.ListenerTags ?? []).some((x) => x.CurrentValue === null)
		) {
			setListenerTagRowStyling(alertStyling);
			return;
		} else if (props.isActiveStep && !props.step.Status) {
			setListenerTagRowStyling(waitStyling);
		}
	}, [
		props.step.Status,
		props.step.ListenerTags,
		props.isActiveStep,
		props.step.IsSkipped,
		props.step.IsOverridden,
		isDark,
	]);

	const listenerTags = props.step.ListenerTags ?? [];
	if (listenerTags.length === 0 || !props.showMeasurements) {
		return <></>;
	}

	return (
		<TagContainer>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "1rem",
				}}
				className="grid-row-signal-container"
			>
				{listenerTags.map((listenerTag, key) => (
					<ListenerTagRow
						key={key}
						listenerTag={listenerTag}
						step={props.step}
						isLast={key === listenerTags.length - 1}
						checklistId={props.activeSchemeId}
						styling={ListenerTagRowStyling}
					/>
				))}
			</Box>
		</TagContainer>
	);
};

export default StepMeasurementListeners;

export const TagContainer = styled(Box)({
	width: "100%",
	position: "relative",
	borderRadius: 4,
	padding: "2px 5px",
	margin: "0 auto",
});
